import { CaretLeftIcon, CaretRightIcon, SparkleIcon, XIcon } from '@phosphor-icons/react';
import Button from '@/components/ui/Button';

export const PRODUCT_NOTE_BACKGROUNDS = [
  { value: 'light_blue', label: 'Blue', className: 'bg-primary-lightblue' },
  { value: 'sky', label: 'Sky', className: 'bg-oi-sky-100' },
  { value: 'teal', label: 'Teal', className: 'bg-oi-teal-100' },
  { value: 'yellow', label: 'Yellow', className: 'bg-oi-yellow-100' },
  { value: 'orange', label: 'Orange', className: 'bg-oi-orange-100' },
  { value: 'pink', label: 'Pink', className: 'bg-oi-pink-100' }
] as const;

export type ProductNoteBackground = (typeof PRODUCT_NOTE_BACKGROUNDS)[number]['value'];

export function productNoteBackgroundClass(background: string | undefined) {
  return PRODUCT_NOTE_BACKGROUNDS.find((item) => item.value === background)?.className ?? 'bg-primary-lightblue';
}

/** Desktop home-page product note. The same markup is used for the admin preview. */
export default function ProductNoteBanner({
  title,
  body,
  background = 'light_blue',
  onTry,
  onDismiss,
  actionLabel = 'Try it',
  showDismiss = true,
  pageCount = 0,
  pageIndex = 0,
  onPage
}: {
  title: string;
  body: string;
  background?: ProductNoteBackground | string;
  onTry?: () => void;
  onDismiss?: () => void;
  actionLabel?: string;
  showDismiss?: boolean;
  pageCount?: number;
  pageIndex?: number;
  onPage?: (index: number) => void;
}) {
  return (
    <div
      className={`flex min-w-0 items-center gap-3 overflow-hidden rounded-xl border border-border-default px-3.5 py-1.5 ${productNoteBackgroundClass(background)}`}
    >
      <p className="inline-flex shrink-0 items-center gap-1.5 text-xs text-content-subtle">
        <SparkleIcon size={14} weight="fill" className="text-secondary-blue" />
        Product note
      </p>
      <p className="min-w-0 truncate text-sm text-content-default">
        <span className="font-semibold">{title}</span>
        <span className="text-content-subtle"> — {body}</span>
      </p>
      <div className="ml-auto flex shrink-0 items-center gap-2">
        {pageCount > 1 && onPage ? (
          <>
            <div className="flex items-center gap-1.5">
              {Array.from({ length: pageCount }, (_, itemIndex) => (
                <button
                  key={itemIndex}
                  type="button"
                  aria-label={`Show product note ${itemIndex + 1}`}
                  aria-current={itemIndex === pageIndex ? 'true' : undefined}
                  onClick={() => onPage(itemIndex)}
                  className={
                    itemIndex === pageIndex
                      ? 'h-1.5 w-4 rounded-full bg-secondary-blue'
                      : 'size-1.5 rounded-full bg-bluegray-300'
                  }
                />
              ))}
            </div>
            <div className="flex items-center">
              <button
                type="button"
                aria-label="Previous product note"
                disabled={pageIndex === 0}
                onClick={() => onPage(pageIndex - 1)}
                className="flex size-6 items-center justify-center text-content-subtle disabled:opacity-40"
              >
                <CaretLeftIcon size={14} />
              </button>
              <button
                type="button"
                aria-label="Next product note"
                disabled={pageIndex >= pageCount - 1}
                onClick={() => onPage(pageIndex + 1)}
                className="flex size-6 items-center justify-center text-content-subtle disabled:opacity-40"
              >
                <CaretRightIcon size={14} />
              </button>
            </div>
          </>
        ) : null}
        <Button variant="primary" size="md" text={actionLabel} onClick={onTry} />
        {showDismiss ? (
          <button
            type="button"
            aria-label="Dismiss product note"
            onClick={onDismiss}
            className="flex size-7 items-center justify-center rounded-md text-content-subtle hover:bg-white/70"
          >
            <XIcon size={14} />
          </button>
        ) : null}
      </div>
    </div>
  );
}
